import React, { useEffect, useState, Children, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plane, MapPin, Clock, ShieldCheck, PackageCheck, ClipboardList, FileCheck, CreditCard, ArrowRight, Truck, CheckCircle, Award, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { ShippingCalculator } from '../components/ShippingCalculator';
interface HomePageProps {
  onNavigate?: (page: string) => void;
}
// Hero slider images
const heroImages = [{
  url: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
  alt: 'Modern logistics warehouse'
}, {
  url: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
  alt: 'Cargo shipping containers'
}, {
  url: 'https://images.unsplash.com/photo-1553413077-190dd305871c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
  alt: 'Package delivery service'
}];
export function HomePage({
  onNavigate
}: HomePageProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  // Preload all images on mount
  useEffect(() => {
    const preloadImages = async () => {
      const promises = heroImages.map(image => {
        return new Promise<void>(resolve => {
          const img = new Image();
          img.src = image.url;
          img.onload = () => resolve();
          img.onerror = () => resolve(); // Resolve even on error to not block
        });
      });
      await Promise.all(promises);
      setImagesLoaded(true);
    };
    preloadImages();
  }, []);
  // Auto-advance slider
  useEffect(() => {
    if (!imagesLoaded) return;
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [imagesLoaded]);
  const nextSlide = () => setCurrentSlide(prev => (prev + 1) % heroImages.length);
  const prevSlide = () => setCurrentSlide(prev => (prev - 1 + heroImages.length) % heroImages.length);
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  const staggerContainer = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  return <div className="flex flex-col bg-white font-sans text-gray-900" data-id="element-2011">
      {/* 1. HERO SECTION with Full-Screen Image Slider */}
      <section className="relative min-h-[600px] lg:min-h-[700px] text-white overflow-hidden -mt-20 sm:-mt-[104px] pt-20 sm:pt-[104px]" data-id="element-2012">
        {/* Background Image Slider - All images rendered, only opacity changes */}
        <div className="absolute inset-0" data-id="element-2013">
          {heroImages.map((image, index) => <div key={index} className={`absolute inset-0 transition-opacity duration-700 ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`} data-id="element-2014">
              <img src={image.url} alt={image.alt} className="absolute inset-0 w-full h-full object-cover" loading={index === 0 ? 'eager' : 'lazy'} data-id="element-2015" />
            </div>)}
          {/* Enhanced dark overlay for better text readability */}
          <div className="absolute inset-0 bg-gray-900/60" data-id="element-2016"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-gray-900/50 to-gray-900/40" data-id="element-2017"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 via-transparent to-gray-900/40" data-id="element-2018"></div>
        </div>

        {/* Slider Navigation - Bottom */}
        <div className="absolute bottom-4 sm:bottom-10 left-1/2 -translate-x-1/2 z-20 flex items-center gap-4" data-id="element-2019">
          <button onClick={prevSlide} className="w-10 h-10 rounded-full bg-black/30 backdrop-blur-sm border border-white/30 flex items-center justify-center text-white hover:bg-black/40 transition-colors" data-id="element-2020">
            <ChevronLeft className="w-5 h-5" data-id="element-2021" />
          </button>
          <div className="flex gap-2" data-id="element-2022">
            {heroImages.map((_, index) => <button key={index} onClick={() => setCurrentSlide(index)} className={`h-2 rounded-full transition-all duration-300 ${index === currentSlide ? 'bg-white w-8' : 'bg-white/50 w-2 hover:bg-white/70'}`} data-id="element-2023" />)}
          </div>
          <button onClick={nextSlide} className="w-10 h-10 rounded-full bg-black/30 backdrop-blur-sm border border-white/30 flex items-center justify-center text-white hover:bg-black/40 transition-colors" data-id="element-2024">
            <ChevronRight className="w-5 h-5" data-id="element-2025" />
          </button>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-24 sm:pt-20 sm:pb-20 lg:pt-28 lg:pb-28" data-id="element-2026">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-2027">
            {/* Left: Hero Content */}
            <motion.div initial="hidden" animate="visible" variants={fadeIn} data-id="element-2028">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-black/40 border border-white/30 text-white text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-2029">
                <span className="relative flex h-2 w-2" data-id="element-2030">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-400 opacity-75" data-id="element-2031"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-500" data-id="element-2032"></span>
                </span>
                Fast & Reliable International Shipping
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 leading-[1.1]" style={{
              textShadow: '0 2px 4px rgba(0,0,0,0.5), 0 4px 12px rgba(0,0,0,0.4), 0 0 40px rgba(0,0,0,0.3)'
            }} data-id="element-2033">
                <span className="text-white" data-id="element-2034">Ship from USA to</span>{' '}
                <span className="text-brand-300" style={{
                textShadow: '0 2px 4px rgba(0,0,0,0.5), 0 4px 12px rgba(0,0,0,0.4)'
              }} data-id="element-2035">
                  Lagos, Nigeria
                </span>{' '}
                <span className="text-white" data-id="element-2036">with ease</span>
              </h1>

              <p className="text-lg sm:text-xl text-white mb-8 max-w-xl leading-relaxed" style={{
              textShadow: '0 1px 3px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.3)'
            }} data-id="element-2037">
                Experience seamless logistics with TaskRunner. We handle
                customs, documentation, and delivery so you can focus on what
                matters most.
              </p>

              <div className="flex flex-col sm:flex-row gap-4" data-id="element-2038">
                <Button size="lg" className="bg-brand-500 hover:bg-brand-600 text-white border-none shadow-lg shadow-brand-500/25 px-8" onClick={() => onNavigate?.('shipping')} data-id="element-2039">
                  Start Shipping Now
                  <ArrowRight className="ml-2 w-5 h-5" data-id="element-2040" />
                </Button>
              </div>
            </motion.div>

            {/* Right: Calculator Widget */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="hidden lg:block" data-id="element-2041">
              <ShippingCalculator compact onStartShipping={() => onNavigate?.('shipping')} data-id="element-2042" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mobile Calculator Section */}
      <section className="lg:hidden py-8 bg-gray-50" data-id="element-2043">
        <div className="max-w-xl mx-auto px-4" data-id="element-2044">
          <ShippingCalculator compact onStartShipping={() => onNavigate?.('shipping')} data-id="element-2045" />
        </div>
      </section>

      {/* 2. SERVICE CARDS SECTION */}
      <section className="py-20 lg:py-24 bg-gray-50" data-id="element-2046">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2047">
          <div className="text-center mb-14" data-id="element-2048">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-id="element-2049">
              Our Core Service
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg" data-id="element-2050">
              We specialize in one route and do it exceptionally well — shipping
              from the USA to Lagos, Nigeria.
            </p>
          </div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="max-w-3xl mx-auto" data-id="element-2051">
            {/* Main Service Card: USA to Lagos */}
            <motion.div variants={fadeIn} className="group bg-white rounded-2xl p-8 sm:p-10 shadow-card hover:shadow-card-hover transition-all duration-300 border border-gray-200 cursor-pointer" onClick={() => onNavigate?.('shipping')} data-id="element-2052">
              <div className="flex flex-col sm:flex-row sm:items-start gap-6" data-id="element-2053">
                <div className="w-16 h-16 bg-brand-50 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-brand-100 transition-colors" data-id="element-2054">
                  <Plane className="w-8 h-8 text-brand-600" data-id="element-2055" />
                </div>
                <div className="flex-1" data-id="element-2056">
                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 group-hover:text-brand-600 transition-colors" data-id="element-2057">
                    USA to Lagos, Nigeria
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed text-lg" data-id="element-2058">
                    Direct air and sea freight from any US location to Lagos.
                    Weekly departures with expedited customs clearing and
                    door-to-door delivery options.
                  </p>

                  {/* Features included */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6" data-id="element-2059">
                    {['Drop off at any US location', 'Customs clearance included', 'Real-time tracking', 'Cargo insurance available', '7-14 days delivery', 'Door-to-door in Lagos'].map(feature => <div key={feature} className="flex items-center gap-2 text-sm text-gray-600" data-id="element-2060">
                        <CheckCircle className="w-4 h-4 text-brand-500 flex-shrink-0" data-id="element-2061" />
                        <span data-id="element-2062">{feature}</span>
                      </div>)}
                  </div>

                  {/* Optional Interstate Add-on */}
                  <div className="bg-secondary-50 rounded-xl p-4 border border-secondary-100" data-id="element-2063">
                    <div className="flex items-start gap-3" data-id="element-2064">
                      <div className="w-10 h-10 bg-secondary-100 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-2065">
                        <Truck className="w-5 h-5 text-secondary-700" data-id="element-2066" />
                      </div>
                      <div data-id="element-2067">
                        <h4 className="font-semibold text-gray-900 mb-1" data-id="element-2068">
                          Optional: Interstate Delivery
                        </h4>
                        <p className="text-sm text-gray-600" data-id="element-2069">
                          Need delivery beyond Lagos? Add interstate shipping to
                          any Nigerian state for an additional fee.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center text-brand-600 font-semibold mt-6 group-hover:translate-x-1 transition-transform" data-id="element-2070">
                    Start shipping now <ArrowRight className="ml-2 w-5 h-5" data-id="element-2071" />
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 3. ABOUT US SECTION */}
      <section className="py-20 lg:py-24 bg-white" data-id="element-2072">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2073">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-2074">
            {/* Left: Image */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="relative" data-id="element-2075">
              <div className="rounded-2xl overflow-hidden shadow-xl" data-id="element-2076">
                <img src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" alt="TaskRunner logistics operations" className="w-full h-auto" data-id="element-2077" />
              </div>
              {/* Floating Stats */}
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-lg p-4 border border-gray-100 hidden sm:block" data-id="element-2078">
                <div className="flex items-center gap-3" data-id="element-2079">
                  <div className="w-12 h-12 bg-brand-50 rounded-lg flex items-center justify-center" data-id="element-2080">
                    <Award className="w-6 h-6 text-brand-600" data-id="element-2081" />
                  </div>
                  <div data-id="element-2082">
                    <div className="text-2xl font-bold text-gray-900" data-id="element-2083">10+</div>
                    <div className="text-sm text-gray-500" data-id="element-2084">
                      Years Experience
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right: Content */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-2085">
              <span className="inline-block px-4 py-1.5 bg-brand/10 text-brand text-sm font-semibold rounded-full mb-6" data-id="element-2086">
                About TaskRunner
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6" data-id="element-2087">
                Your Trusted Partner for USA to Nigeria Shipping
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-2088">
                For over a decade, TaskRunner has been the bridge connecting
                families and businesses between the United States and Nigeria.
                We understand the importance of every package — whether it's a
                care package for loved ones or critical business supplies.
              </p>
              <p className="text-gray-600 mb-8 leading-relaxed" data-id="element-2089">
                Our dedicated team ensures your shipments arrive safely and on
                time, with full transparency every step of the way. We've built
                our reputation on reliability, affordability, and exceptional
                customer service.
              </p>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-6 mb-8" data-id="element-2090">
                <div className="text-center" data-id="element-2091">
                  <div className="text-2xl sm:text-3xl font-bold text-brand-600" data-id="element-2092">
                    50K+
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500" data-id="element-2093">
                    Packages Delivered
                  </div>
                </div>
                <div className="text-center" data-id="element-2094">
                  <div className="text-2xl sm:text-3xl font-bold text-brand-600" data-id="element-2095">
                    99.9%
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500" data-id="element-2096">
                    Success Rate
                  </div>
                </div>
                <div className="text-center" data-id="element-2097">
                  <div className="text-2xl sm:text-3xl font-bold text-brand-600" data-id="element-2098">
                    4
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500" data-id="element-2099">
                    US Locations
                  </div>
                </div>
              </div>

              <Button variant="outline" size="lg" onClick={() => onNavigate?.('about')} className="group" data-id="element-2100">
                Explore More
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-2101" />
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. TRUST INDICATORS SECTION */}
      <section className="py-20 lg:py-24 bg-gray-50" data-id="element-2102">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2103">
          <div className="text-center mb-14" data-id="element-2104">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-id="element-2105">
              Why Choose TaskRunner?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-2106">
              We're committed to making your shipping experience seamless and
              stress-free.
            </p>
          </div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-10" data-id="element-2107">
            {/* Trust 1 */}
            <motion.div variants={fadeIn} className="flex flex-col items-center text-center" data-id="element-2108">
              <div className="w-16 h-16 bg-brand-50 rounded-2xl flex items-center justify-center mb-6" data-id="element-2109">
                <Clock className="w-8 h-8 text-brand-600" data-id="element-2110" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2111">
                Fast Delivery
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-2112">
                Air freight arrives in 7-14 business days. We value your time
                and stick to our schedules.
              </p>
            </motion.div>

            {/* Trust 2 */}
            <motion.div variants={fadeIn} className="flex flex-col items-center text-center" data-id="element-2113">
              <div className="w-16 h-16 bg-brand-50 rounded-2xl flex items-center justify-center mb-6" data-id="element-2114">
                <MapPin className="w-8 h-8 text-brand-600" data-id="element-2115" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2116">
                Real-Time Tracking
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-2117">
                Know exactly where your package is at every step of the journey
                with our tracking system.
              </p>
            </motion.div>

            {/* Trust 3 */}
            <motion.div variants={fadeIn} className="flex flex-col items-center text-center" data-id="element-2118">
              <div className="w-16 h-16 bg-brand-50 rounded-2xl flex items-center justify-center mb-6" data-id="element-2119">
                <ShieldCheck className="w-8 h-8 text-brand-600" data-id="element-2120" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2121">
                Secure Shipping
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-2122">
                Your items are insured and handled with care. 99.9% safe
                delivery rate for all cargo.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 5. HOW IT WORKS SECTION */}
      <section className="py-20 lg:py-24 bg-secondary-900 text-white relative overflow-hidden" data-id="element-2123">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-2124">
          <div className="text-center mb-16" data-id="element-2125">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-id="element-2126">
              How It Works
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto text-lg" data-id="element-2127">
              Shipping with TaskRunner is simple. Just follow these four easy
              steps.
            </p>
          </div>

          <div className="relative" data-id="element-2128">
            {/* Connecting Line (Desktop) */}
            <div className="hidden md:block absolute top-12 left-[12.5%] right-[12.5%] h-0.5 bg-secondary-700" data-id="element-2129"></div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-4 gap-10" data-id="element-2130">
              {/* Step 1 */}
              <motion.div variants={fadeIn} className="relative flex flex-col items-center text-center group" data-id="element-2131">
                <div className="w-24 h-24 bg-secondary-800 rounded-2xl border border-secondary-700 flex items-center justify-center mb-6 shadow-lg group-hover:-translate-y-1 transition-transform duration-300" data-id="element-2132">
                  <ClipboardList className="w-10 h-10 text-brand-400" data-id="element-2133" />
                  <div className="absolute -top-3 -right-3 w-8 h-8 bg-brand-500 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg" data-id="element-2134">
                    1
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-3" data-id="element-2135">Fill Form</h3>
                <p className="text-gray-400 text-sm leading-relaxed" data-id="element-2136">
                  Provide details about your shipment and destination.
                </p>
              </motion.div>

              {/* Step 2 */}
              <motion.div variants={fadeIn} className="relative flex flex-col items-center text-center group" data-id="element-2137">
                <div className="w-24 h-24 bg-secondary-800 rounded-2xl border border-secondary-700 flex items-center justify-center mb-6 shadow-lg group-hover:-translate-y-1 transition-transform duration-300" data-id="element-2138">
                  <FileCheck className="w-10 h-10 text-brand-400" data-id="element-2139" />
                  <div className="absolute -top-3 -right-3 w-8 h-8 bg-brand-500 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg" data-id="element-2140">
                    2
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-3" data-id="element-2141">Review Summary</h3>
                <p className="text-gray-400 text-sm leading-relaxed" data-id="element-2142">
                  Check the quote and confirm your shipment details.
                </p>
              </motion.div>

              {/* Step 3 */}
              <motion.div variants={fadeIn} className="relative flex flex-col items-center text-center group" data-id="element-2143">
                <div className="w-24 h-24 bg-secondary-800 rounded-2xl border border-secondary-700 flex items-center justify-center mb-6 shadow-lg group-hover:-translate-y-1 transition-transform duration-300" data-id="element-2144">
                  <CreditCard className="w-10 h-10 text-brand-400" data-id="element-2145" />
                  <div className="absolute -top-3 -right-3 w-8 h-8 bg-brand-500 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg" data-id="element-2146">
                    3
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-3" data-id="element-2147">Make Payment</h3>
                <p className="text-gray-400 text-sm leading-relaxed" data-id="element-2148">
                  Pay securely using your preferred payment method.
                </p>
              </motion.div>

              {/* Step 4 */}
              <motion.div variants={fadeIn} className="relative flex flex-col items-center text-center group" data-id="element-2149">
                <div className="w-24 h-24 bg-secondary-800 rounded-2xl border border-secondary-700 flex items-center justify-center mb-6 shadow-lg group-hover:-translate-y-1 transition-transform duration-300" data-id="element-2150">
                  <PackageCheck className="w-10 h-10 text-brand-400" data-id="element-2151" />
                  <div className="absolute -top-3 -right-3 w-8 h-8 bg-brand-500 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg" data-id="element-2152">
                    4
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-3" data-id="element-2153">Track Shipment</h3>
                <p className="text-gray-400 text-sm leading-relaxed" data-id="element-2154">
                  Monitor your package in real-time until delivery.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 6. FINAL CTA SECTION */}
      <section className="py-20 lg:py-24 bg-white" data-id="element-2155">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2156">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="bg-gradient-to-br from-brand-600 to-brand-700 rounded-3xl p-10 sm:p-16 shadow-xl" data-id="element-2157">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" data-id="element-2158">
              Ready to ship your package?
            </h2>
            <p className="text-xl text-brand-100 mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-2159">
              Join thousands of satisfied customers who trust TaskRunner for
              their USA to Nigeria shipping needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2160">
              <Button variant="white" size="lg" className="px-10 py-4 text-lg h-auto" onClick={() => onNavigate?.('shipping')} data-id="element-2161">
                Start Shipping Now
              </Button>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10 px-10 py-4 text-lg h-auto" onClick={() => onNavigate?.('contact')} data-id="element-2162">
                Contact Us
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}